import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Search,
  ScanLine,
  User,
  Trash2,
  Minus,
  Plus,
  Package,
  Clock,
  CreditCard,
  Sparkles,
  Tag,
  ChevronRight,
  X,
  LogOut,
  RotateCcw,
  History,
  ArrowLeftRight,
} from "lucide-react";
import PosTransactionHistoryModal from "./components/PosTransactionHistoryModal";
import CountryCodeInput from '../../components/common/CountryCodeInput';
import { withCountryCode } from '../../utils/customerPhone';
import logo from "../../assets/logo.png";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
  closeSession,
  createCustomer,
  createSale,
  createPosReturn,
  getCurrentSession,
  getMyCounters,
  openSession,
  searchAgents,
  searchCustomers,
  searchProducts,
  validateCoupon,
  cancelHeldBill,
  createHeldBill,
  getHeldBill,
  getHeldBills,
  searchEligibleReturnItems,
  createExchange,
} from "../../services/pos";
import { hasPermission } from "../../utils/permissions";
import PosReturnModal from "./components/PosReturnModal";
import PosCloseShiftModal from "./components/PosCloseShiftModal";
import PosBillItemsTable from "./components/PosBillItemsTable";
import PosGstTotal from './components/PosGstTotal';

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const imagePlaceholder =
  "https://placehold.co/100x100/F8F6F5/7A2238?text=CHARMS";

function Modal({ children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      {children}
    </div>
  );
}

export default function POS() {
  const navigate = useNavigate();
  const { accessToken, user, logout } = useAuth();
  const barcodeInputRef = useRef(null);
  const [session, setSession] = useState(null);
  const [counters, setCounters] = useState([]);
  const [barcode, setBarcode] = useState("");
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [lotAllocationMethod, setLotAllocationMethod] = useState("FIFO");
  const [cart, setCart] = useState([]);
  const [loyaltyPointsToRedeem, setLoyaltyPointsToRedeem] = useState("");
  const [couponInput, setCouponInput] = useState("");
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [recentScans, setRecentScans] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customer, setCustomer] = useState(null);
  const [agents, setAgents] = useState([]);
  const [agent, setAgent] = useState(null);
  const [agentSearch, setAgentSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [showCreateCustomer, setShowCreateCustomer] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showTransactionHistory, setShowTransactionHistory] = useState(false);
  const [customerCountryCode, setCustomerCountryCode] = useState('+91');
  const [newCustomer, setNewCustomer] = useState({
    fullName: "",
    phone: "",
    email: "",
  });
  const [heldBills, setHeldBills] = useState([]);
  const [loadingHeldBills, setLoadingHeldBills] = useState(false);
  const [showHoldBillModal, setShowHoldBillModal] = useState(false);
  const [holdNote, setHoldNote] = useState("");
  const [activeHeldBillId, setActiveHeldBillId] = useState(null);
  const [showOpen, setShowOpen] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [selectedCounterId, setSelectedCounterId] = useState("");
  const [openingCash, setOpeningCash] = useState("0");
  const [actualCash, setActualCash] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("CASH");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentReference, setPaymentReference] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [eligibleReturnItems, setEligibleReturnItems] = useState([]);
  const [loadingEligibleReturns, setLoadingEligibleReturns] = useState(false);

  const loadSession = useCallback(async () => {
    const [current, assignedCounters] = await Promise.all([
      getCurrentSession(accessToken),
      getMyCounters(accessToken),
    ]);
    setSession(current);
    setCounters(assignedCounters);
    if (!current && assignedCounters.length)
      setSelectedCounterId(assignedCounters[0].id);
  }, [accessToken]);

  const loadHeldBills = useCallback(async () => {
    if (!session) {
      setHeldBills([]);
      return;
    }

    setLoadingHeldBills(true);

    try {
      const data = await getHeldBills(accessToken);
      setHeldBills(data);
    } catch (requestError) {
      setError(requestError.message || "Unable to load held bills");
    } finally {
      setLoadingHeldBills(false);
    }
  }, [accessToken, session]);

  const loadCustomers = useCallback(
    async (query = "") => {
      const data = await searchCustomers(query, accessToken);
      setCustomers(data);
    },
    [accessToken],
  );

  useEffect(() => {
    loadHeldBills();
  }, [loadHeldBills]);

  useEffect(() => {
    setLoyaltyPointsToRedeem("");
  }, [customer?.id]);

  useEffect(() => {
    setAppliedCoupon(null);
  }, [customer?.id, cart]);

  const loadAgents = useCallback(
    async (query = "") => {
      const data = await searchAgents(query, accessToken);
      setAgents(data);
    },
    [accessToken],
  );

  useEffect(() => {
    Promise.all([loadSession(), loadCustomers()])
      .catch((requestError) =>
        setError(requestError.message || "Unable to load POS"),
      )
      .finally(() => setLoading(false));
  }, [loadCustomers, loadSession]);

  useEffect(() => {
    if (!session) {
      setAgents([]);
      return undefined;
    }

    const timer = setTimeout(() => {
      loadAgents(agentSearch).catch((requestError) =>
        setError(requestError.message || "Unable to search agents"),
      );
    }, 250);

    return () => clearTimeout(timer);
  }, [agentSearch, loadAgents, session]);

  useEffect(() => {
    if (!session || !search.trim()) {
      setResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      try {
        const response = await searchProducts(search, accessToken);
        setResults(response.products || []);
        setLotAllocationMethod(response.lotAllocationMethod || "FIFO");
      } catch (requestError) {
        setError(requestError.message || "Unable to search products");
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [accessToken, search, session]);

  useEffect(() => {
    const timer = setTimeout(
      () => loadCustomers(customerSearch).catch((e) => setError(e.message)),
      250,
    );
    return () => clearTimeout(timer);
  }, [customerSearch, loadCustomers]);

  const addToCart = (product) => {
    if (!product.canSell)
      return setError("This product is out of stock at the current store");
    setCart((previous) => {
      const existing = previous.find(
        (item) =>
          item.variantId === product.variantId &&
          (lotAllocationMethod !== "NORMAL" ||
            item.inventoryLotId === product.availableLots?.[0]?.id) &&
          (item.lineType || "SALE") === "SALE",
      );
      if (existing) {
        if (
          product.availableQuantity !== null &&
          existing.quantity >= product.availableQuantity
        ) {
          setError("You cannot add more than available store stock");
          return previous;
        }
        return previous.map((item) =>
          item.rowId === existing.rowId
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }
      return [
        ...previous,
        {
          ...product,
          rowId: `sale-${product.variantId}`,
          lineType: "SALE",
          sourceSaleItemId: null,
          quantity: 1,
          unitPrice: Number(product.unitPrice),
          listPrice: Number(product.unitPrice),
          availableLots: product.availableLots || [],
          inventoryLotId:
            lotAllocationMethod === "NORMAL"
              ? product.availableLots?.[0]?.id || null
              : null,
          agentEmployeeId: null,
          priceOverrideReason: "",
          image: imagePlaceholder,
        },
      ];
    });
    setRecentScans((previous) =>
      [
        {
          id: `${product.variantId}-${Date.now()}`,
          product: product.product.name,
          time: new Date().toLocaleTimeString(),
        },
        ...previous,
      ].slice(0, 5),
    );
    setBarcode("");
    setSearch("");
    setResults([]);
    setTimeout(() => barcodeInputRef.current?.focus(), 0);
  };

  const findEligibleReturns = async (item) => {
    setLoadingEligibleReturns(true);
    setError("");

    try {
      const response = await searchEligibleReturnItems(
        item.sku || item.barcode,
        customer?.id,
        accessToken,
      );

      setEligibleReturnItems(response?.eligibleItems || []);
    } catch (requestError) {
      setError(
        requestError.message || "Unable to find eligible previous sales.",
      );
    } finally {
      setLoadingEligibleReturns(false);
    }
  };

  const addReturnToBill = (eligibleItem) => {
    setCart((previous) => {
      const existingReturn = previous.find(
        (item) =>
          item.rowId === `return-${eligibleItem.sourceSaleItemId}` &&
          item.lineType === "RETURN",
      );

      if (existingReturn) {
        if (existingReturn.quantity >= existingReturn.maxQuantity) {
          setError("This item has reached its returnable quantity.");
          return previous;
        }

        return previous.map((item) =>
          item.rowId === existingReturn.rowId
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }

      return [
        ...previous,
        {
          variantId: eligibleItem.variant.id,
          rowId: `return-${eligibleItem.sourceSaleItemId}`,
          sourceSaleItemId: eligibleItem.sourceSaleItemId,
          lineType: "RETURN",

          product: {
            name: eligibleItem.productName,
            gstRuleGroup: null,
            season: eligibleItem.season || null,
          },

          taxRate: Number(eligibleItem.taxRate || 0),

          sku: eligibleItem.sku,
          barcode: eligibleItem.variant.barcode || null,
          variantName: eligibleItem.variantName || "Default Variant",

          color: eligibleItem.color || null,
          size: eligibleItem.size || null,

          quantity: 1,
          maxQuantity: Number(eligibleItem.quantityReturnable),

          // Historic values — cashier cannot edit return prices.
          unitPrice: Number(eligibleItem.returnUnitAmount),
          listPrice: Number(eligibleItem.returnUnitAmount),

          loyaltyDiscountPerUnit:
            Number(eligibleItem.loyaltyDiscountAmount || 0) /
            Math.max(Number(eligibleItem.quantitySold || 1), 1),

          receiptNumber: eligibleItem.receiptNumber,

          // Show original sale agent, but do not permit changing it.
          agentEmployeeId: eligibleItem.agentEmployee?.id || null,
          agentEmployee: eligibleItem.agentEmployee || null,

          priceOverrideReason: "",
        },
      ];
    });

    setAppliedCoupon(null);
    setCouponInput("");
    setLoyaltyPointsToRedeem("");
    setEligibleReturnItems([]);
    setSearch("");
    setResults([]);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } finally {
      navigate("/pos/login", { replace: true });
    }
  };

  const scanBarcode = async () => {
    if (!barcode.trim()) return;
    try {
      const response = await searchProducts(barcode.trim(), accessToken);
      setLotAllocationMethod(response.lotAllocationMethod || "FIFO");
      const exact = (response.products || []).find(
        (item) =>
          item.barcode === barcode.trim() || item.sku === barcode.trim(),
      );
      if (exact) addToCart(exact);
      else {
        setResults(response.products || []);
        setSearch(barcode.trim());
        if (!response.products?.length) setError("Product not found");
      }
    } catch (requestError) {
      setError(requestError.message || "Unable to scan product");
    }
  };

  const updateQuantity = (rowId, value) => {
    setCart((previous) =>
      previous.map((item) => {
        if (item.rowId !== rowId) return item;

        const quantity = Math.max(1, Number(value) || 1);
        const capped =
          item.maxQuantity !== null && item.maxQuantity !== undefined
            ? Math.min(quantity, item.maxQuantity)
            : item.availableQuantity !== null &&
                item.availableQuantity !== undefined
              ? Math.min(quantity, item.availableQuantity)
              : quantity;

        return { ...item, quantity: capped };
      }),
    );
  };

  const updatePrice = (rowId, value) => {
    // Preserve incomplete, valid decimal input while the cashier is typing.
    if (value !== "" && !/^\d*(?:\.\d{0,2})?$/.test(value)) return;

    setCart((previous) =>
      previous.map((item) =>
        item.rowId === rowId && item.lineType !== "RETURN"
          ? { ...item, unitPrice: value }
          : item,
      ),
    );
  };

  const updatePriceOverrideReason = (rowId, value) => {
    setCart((previous) =>
      previous.map((item) =>
        item.rowId === rowId
          ? { ...item, priceOverrideReason: value }
          : item,
      ),
    );
  };

  const updateLot = (rowId, inventoryLotId) => {
    setCart((previous) =>
      previous.map((item) => {
        if (item.rowId !== rowId || item.lineType === "RETURN") return item;

        const selectedLot = (item.availableLots || []).find(
          (lot) => lot.id === inventoryLotId,
        );

        if (!selectedLot) return item;

        return {
          ...item,
          inventoryLotId,
          unitPrice: Number(selectedLot.posPrice),
          listPrice: Number(selectedLot.posPrice),
          availableQuantity: Number(selectedLot.availableQuantity),
          quantity: Math.min(
            Number(item.quantity || 1),
            Number(selectedLot.availableQuantity),
          ),
          priceOverrideReason: "",
        };
      }),
    );
  };

  const saleBillLines = useMemo(
    () => cart.filter((item) => (item.lineType || "SALE") === "SALE"),
    [cart],
  );

  const returnBillLines = useMemo(
    () => cart.filter((item) => item.lineType === "RETURN"),
    [cart],
  );

  const hasReturnLines = returnBillLines.length > 0;

  const saleItemsTotal = useMemo(
    () =>
      saleBillLines.reduce(
        (sum, item) => sum + Number(item.unitPrice) * Number(item.quantity),
        0,
      ),
    [saleBillLines],
  );

  const returnItemsTotal = useMemo(
    () =>
      returnBillLines.reduce(
        (sum, item) => sum + Number(item.unitPrice) * Number(item.quantity),
        0,
      ),
    [returnBillLines],
  );

  const subtotal = saleItemsTotal;

  const availableLoyaltyPoints = Number(
    customer?.loyaltyAccount?.pointsBalance || 0,
  );

  const couponDiscountAmount = hasReturnLines
    ? 0
    : Number(appliedCoupon?.discountAmount || 0);

  const amountAfterCoupon = Math.max(0, subtotal - couponDiscountAmount);

  const requestedLoyaltyPoints = Math.max(
    0,
    Math.floor(Number(loyaltyPointsToRedeem || 0)),
  );

  const redeemableLoyaltyPoints =
    customer && !hasReturnLines
      ? Math.min(
          requestedLoyaltyPoints,
          availableLoyaltyPoints,
          Math.floor(amountAfterCoupon),
        )
      : 0;

  const loyaltyDiscountAmount = redeemableLoyaltyPoints;

  const exactGrandTotal = Math.max(
    0,
    amountAfterCoupon - loyaltyDiscountAmount,
  );
  // Customer bills settle in whole rupees. Keep the adjustment visible rather
  // than folding it into GST or a product line.
  const grandTotal = Math.floor(exactGrandTotal + 0.5);
  const saleRoundOffAmount = Number((grandTotal - exactGrandTotal).toFixed(2));

  // Points that were used on the returned item are restored automatically
  // by the backend and first applied against the replacement item.
  const restoredLoyaltyPoints = Math.floor(
    returnBillLines.reduce(
      (sum, item) =>
        sum + Number(item.loyaltyDiscountPerUnit || 0) * Number(item.quantity),
      0,
    ),
  );

  const amountAfterReturnCredit = Math.max(
    0,
    saleItemsTotal - returnItemsTotal,
  );

  const restoredPointsAppliedToReplacement = Math.min(
    restoredLoyaltyPoints,
    amountAfterReturnCredit,
  );

  const unusedRestoredLoyaltyPoints =
    restoredLoyaltyPoints - restoredPointsAppliedToReplacement;

  const settlementTotal = Number(
    (hasReturnLines
      ? saleItemsTotal - returnItemsTotal - restoredPointsAppliedToReplacement
      : grandTotal
    ).toFixed(2),
  );

  const settlementLabel =
    settlementTotal > 0
      ? "Customer Pays"
      : settlementTotal < 0
        ? "Customer Refund"
        : "Equal Exchange";

  const loyaltyPointsEarned =
    customer && settlementTotal > 0 ? Math.floor(settlementTotal * 0.1) : 0;

  const canOverridePrice = hasPermission(user, "pos.override_price");

  const handleOpenSession = async () => {
    setSaving(true);
    setError("");
    try {
      const created = await openSession(
        { counterId: selectedCounterId, openingCash: Number(openingCash || 0) },
        accessToken,
      );
      setSession(created);
      setShowOpen(false);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSession = async () => {
    setSaving(true);
    setError("");
    try {
      await closeSession(
        session.id,
        { actualCash: Number(actualCash) },
        accessToken,
      );
      setSession(null);
      setCart([]);
      setShowClose(false);
      setActualCash("");
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCustomer = async () => {
    setSaving(true);
    setError("");
    try {
      const created = await createCustomer({...newCustomer, phone: withCountryCode(newCustomer.phone, customerCountryCode)}, accessToken);
      setCustomer(created);
      setCustomers((old) => [created, ...old]);
      setShowCreateCustomer(false);
      setNewCustomer({ fullName: "", phone: "", email: "" });
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyCoupon = async () => {
    if (!customer) {
      return setError("Select a customer before applying a coupon");
    }

    if (!cart.length) {
      return setError("Add products before applying a coupon");
    }

    if (!couponInput.trim()) {
      return setError("Enter a coupon code");
    }

    setApplyingCoupon(true);
    setError("");

    try {
      const quote = await validateCoupon(
        {
          customerId: customer.id,
          couponCode: couponInput,
          subtotalAmount: subtotal,
        },
        accessToken,
      );

      setAppliedCoupon(quote);
      setCouponInput(quote.code);
    } catch (requestError) {
      setAppliedCoupon(null);
      setError(requestError.message);
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleHoldBill = async () => {
    if (!cart.length) {
      return setError("Add at least one item before holding a bill");
    }

    setSaving(true);
    setError("");

    try {
      const heldBill = await createHeldBill(
        {
          customerId: customer?.id,
          agentEmployeeId: agent?.id,
          couponCode: appliedCoupon?.code,
          loyaltyPointsToRedeem: redeemableLoyaltyPoints,
          note: holdNote || undefined,
          items: cart.map((item) => ({
            variantId: item.variantId,
            ...(lotAllocationMethod === "NORMAL"
              ? { inventoryLotId: item.inventoryLotId || undefined }
              : {}),
            quantity: item.quantity,
            unitPrice: Number(item.unitPrice),
            priceOverrideReason: item.priceOverrideReason || undefined,
          })),
        },
        accessToken,
      );

      setSuccess(`Bill held successfully — ${heldBill.holdReference}`);

      setCart([]);
      setCustomer(null);
      setAgent(null);
      setAgentSearch("");
      setCouponInput("");
      setAppliedCoupon(null);
      setLoyaltyPointsToRedeem("");
      setHoldNote("");
      setShowHoldBillModal(false);

      await loadHeldBills();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLoadHeldBill = async (heldBillId) => {
    setSaving(true);
    setError("");

    try {
      const heldBill = await getHeldBill(heldBillId, accessToken);
      const snapshot = heldBill.cartSnapshot || {};
      const snapshotItems = snapshot.items || [];

      setCart(
        snapshotItems.map((item, index) => ({
          variantId: item.variantId,
          sku: item.sku || `Held-${index + 1}`,
          barcode: item.barcode || null,
          variantName: item.variantName || "Default Variant",
          quantity: Number(item.quantity),
          unitPrice: Number(item.unitPrice ?? 0),
          listPrice: Number(item.listPrice ?? item.unitPrice ?? 0),
          inventoryLotId: item.inventoryLotId || null,
          priceOverrideReason: item.priceOverrideReason || "",
          availableQuantity: null,
          color: item.color || null,
          size: item.size || null,
          product: {
            name: item.productName || `Held Item ${index + 1}`,
            gstRate: Number(item.gstRate || 0),
            season: item.season || null,
          },
          image: imagePlaceholder,
        })),
      );

      setCustomer(heldBill.customer || null);
      setAgent(heldBill.agentEmployee || null);
      setCouponInput(snapshot.couponCode || "");
      setAppliedCoupon(null);
      setLoyaltyPointsToRedeem(
        snapshot.loyaltyPointsToRedeem
          ? String(snapshot.loyaltyPointsToRedeem)
          : "",
      );
      setActiveHeldBillId(heldBill.id);
      setSuccess(
        `Loaded ${heldBill.holdReference}. Re-apply coupon before payment.`,
      );
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelHeldBill = async (heldBillId) => {
    if (!window.confirm("Cancel this held bill? This cannot be undone.")) {
      return;
    }

    setSaving(true);
    setError("");

    try {
      await cancelHeldBill(heldBillId, accessToken);
      setSuccess("Held bill cancelled");
      await loadHeldBills();
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setSaving(false);
    }
  };

  const handlePayment = async () => {
    if (!cart.length) {
      return setError("Add at least one item before payment");
    }

    const overriddenItem = saleBillLines.find(
      (item) => Number(item.unitPrice) !== Number(item.listPrice),
    );

    if (overriddenItem && !canOverridePrice) {
      return setError("You do not have permission to override item prices");
    }

    if (overriddenItem && !overriddenItem.priceOverrideReason?.trim()) {
      return setError(
        `Enter a price override reason for ${overriddenItem.product.name}`,
      );
    }

    if (hasReturnLines && (appliedCoupon || requestedLoyaltyPoints > 0)) {
      return setError(
        "Coupon and manual loyalty redemption cannot be used with a return or exchange.",
      );
    }

    if (!hasReturnLines) {
      if (!customer && requestedLoyaltyPoints > 0) {
        return setError("Select a customer before redeeming loyalty points");
      }

      if (requestedLoyaltyPoints > availableLoyaltyPoints) {
        return setError(
          `Customer has only ${availableLoyaltyPoints} loyalty points available`,
        );
      }

      if (requestedLoyaltyPoints > Math.floor(subtotal)) {
        return setError("Loyalty redemption cannot exceed the bill amount");
      }
    }

    const paymentRequired = Math.abs(settlementTotal);
    const enteredPaymentAmount =
      paymentAmount === "" ? paymentRequired : Number(paymentAmount);

    if (
      paymentRequired > 0 &&
      Math.abs(enteredPaymentAmount - paymentRequired) > 0.01
    ) {
      return setError(
        `Settlement amount must be exactly ${money(paymentRequired)}`,
      );
    }

    const sourceReceipts = [
      ...new Set(
        returnBillLines.map((item) => item.receiptNumber).filter(Boolean),
      ),
    ];

    if (!saleBillLines.length && sourceReceipts.length !== 1) {
      return setError(
        "Refund-only returns must contain items from one original receipt.",
      );
    }

    setSaving(true);
    setError("");

    try {
      if (saleBillLines.length && returnBillLines.length) {
        const direction =
          settlementTotal > 0
            ? "COLLECT"
            : settlementTotal < 0
              ? "REFUND"
              : "NONE";

        const exchange = await createExchange(
          {
            returnedItems: returnBillLines.map((item) => ({
              sourceSaleItemId: item.sourceSaleItemId,
              quantity: Number(item.quantity),
            })),

            newItems: saleBillLines.map((item) => ({
              variantId: item.variantId,
              quantity: Number(item.quantity),
              agentEmployeeId: item.agentEmployeeId || undefined,
              ...(lotAllocationMethod === "NORMAL"
                ? { inventoryLotId: item.inventoryLotId || undefined }
                : {}),
              ...(Number(item.unitPrice) !== Number(item.listPrice)
                ? {
                    unitPrice: Number(item.unitPrice),
                    priceOverrideReason: item.priceOverrideReason.trim(),
                  }
                : {}),
            })),

            settlement: {
              direction,
              ...(direction !== "NONE"
                ? {
                    payments: [
                      {
                        method: paymentMethod,
                        amount: paymentRequired,
                        reference: paymentReference || undefined,
                      },
                    ],
                  }
                : {}),
            },

            reason: "POS exchange",
          },
          accessToken,
        );

        setSuccess(
          `Exchange completed — ${exchange.exchange.receiptNumber}. ${
            unusedRestoredLoyaltyPoints > 0
              ? `${unusedRestoredLoyaltyPoints} unused loyalty points were restored.`
              : ""
          }`,
        );
      } else if (returnBillLines.length) {
        const posReturn = await createPosReturn(
          {
            receiptNumber: sourceReceipts[0],
            reason: "POS customer return",
            items: returnBillLines.map((item) => ({
              saleItemId: item.sourceSaleItemId,
              quantity: Number(item.quantity),
            })),
            refundPayments:
              paymentRequired > 0
                ? [
                    {
                      method: paymentMethod,
                      amount: paymentRequired,
                      reference: paymentReference || undefined,
                    },
                  ]
                : [],
          },
          accessToken,
        );

        setSuccess(
          `Return completed — ${posReturn.returnReference}. Refund: ${money(
            posReturn.refundAmount,
          )}`,
        );
      } else {
        const sale = await createSale(
          {
            customerId: customer?.id,
            couponCode: appliedCoupon?.code,
            loyaltyPointsToRedeem: redeemableLoyaltyPoints || undefined,
            heldBillId: activeHeldBillId || undefined,

            items: saleBillLines.map((item) => ({
              variantId: item.variantId,
              quantity: Number(item.quantity),
              agentEmployeeId: item.agentEmployeeId || undefined,
              ...(lotAllocationMethod === "NORMAL"
                ? { inventoryLotId: item.inventoryLotId || undefined }
                : {}),
              unitPrice: Number(item.unitPrice),
              ...(Number(item.unitPrice) !== Number(item.listPrice)
                ? {
                    priceOverrideReason: item.priceOverrideReason.trim(),
                  }
                : {}),
            })),

            payments:
              grandTotal > 0
                ? [
                    {
                      method: paymentMethod,
                      amount: grandTotal,
                      reference: paymentReference || undefined,
                    },
                  ]
                : [],
          },
          accessToken,
        );

        const finalTaxAmount = Number(sale.taxAmount || 0);

        setSuccess(
          `Sale completed — ${sale.receiptNumber}. GST included: ${money(
            finalTaxAmount,
          )}. ${loyaltyPointsEarned} loyalty points earned.`,
        );
      }

      setCart([]);
      setCustomer(null);
      setAgent(null);
      setAgentSearch("");
      setLoyaltyPointsToRedeem("");
      setAppliedCoupon(null);
      setCouponInput("");
      setShowPayment(false);
      setPaymentReference("");
      setPaymentAmount("");
      setActiveHeldBillId(null);

      await Promise.all([loadHeldBills(), loadSession()]);
    } catch (requestError) {
      setError(requestError.message || "Unable to complete the bill");
    } finally {
      setSaving(false);
    }
  };

  const cashierName = user?.employee?.fullName || user?.email || "Cashier";
  const selectedCustomer = customer || {
    fullName: "Walk-in Customer",
    phone: "",
    loyaltyAccount: { pointsBalance: 0 },
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#F8F6F5] flex items-center justify-center text-primary-800 font-semibold">
        Loading POS…
      </div>
    );

  return (
    <div className="min-h-screen bg-[#F8F6F5] p-6">
      {(error || success) && (
        <div
          className={`mb-4 flex items-center justify-between rounded-2xl px-5 py-4 ${error ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}`}
        >
          <span>{error || success}</span>
          <button
            onClick={() => {
              setError("");
              setSuccess("");
            }}
          >
            <X size={18} />
          </button>
        </div>
      )}
      <div className="mb-6 flex h-20 items-center justify-between rounded-3xl border border-gray-200 bg-primary-800 px-8 shadow-sm">
        <div className="flex items-center gap-4">
          <img src={logo} alt="Charms" className="h-12 w-12" />
          <div>
            <h1 className="text-2xl font-bold text-white">Charms POS</h1>
            <p className="text-sm text-gray-200">Premium Retail Billing</p>
          </div>
        </div>
        <div className="hidden lg:flex items-center gap-3">
          {session ? (
            <>
              <span className="rounded-full bg-white/10 px-4 py-2 text-sm text-white">
                📍 {session.counter.store.name}
              </span>
              <span className="rounded-full bg-white/10 px-4 py-2 text-sm text-white">
                🟢 Session #{session.sessionNumber}
              </span>
              <span className="rounded-full bg-white/10 px-4 py-2 text-sm text-white">
                {session.counter.name}
              </span>
            </>
          ) : (
            <span className="rounded-full bg-red-500/30 px-4 py-2 text-sm text-white">
              No active shift
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-gray-200">Cashier</p>
            <p className="font-semibold text-gray-200">{cashierName}</p>
          </div>
          <button
            onClick={() =>
              session
                ? (setActualCash(session.expectedCash), setShowClose(true))
                : setShowOpen(true)
            }
            className="rounded-2xl bg-white px-5 py-2.5 font-medium text-primary-800"
          >
            {session ? "End Shift" : "Start Shift"}
          </button>
          <button
            type="button"
            disabled={!session || !hasPermission(user, "pos.return")}
            onClick={() => setShowReturnModal(true)}
            className="inline-flex items-center gap-2 rounded-2xl border border-white/30 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <RotateCcw size={17} />
            Returns
          </button>
          <button
            type="button"
            disabled={!session}
            onClick={() => setShowTransactionHistory(true)}
            className="inline-flex items-center gap-2 rounded-2xl border border-white/30 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/10 disabled:opacity-40"
          >
            <History size={17} />
            History
          </button>
          <button
            onClick={handleLogout}
            className="inline-flex items-center gap-2 rounded-2xl border border-white/30 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-white/10"
          >
            <LogOut size={17} />
            Logout
          </button>
        </div>
      </div>
      <div className="space-y-6">
        <div className="col-span-8 space-y-6">
          <div className="overflow-hidden rounded-[32px] border border-gray-100 bg-white shadow-sm">
            <div className="p-8">
              <label className="mb-3 block text-sm font-semibold uppercase tracking-wider text-gray-500">
                Scan Barcode
              </label>
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <ScanLine
                    className="absolute left-6 top-1/2 -translate-y-1/2 text-primary-800"
                    size={26}
                  />
                  <input
                    ref={barcodeInputRef}
                    autoFocus
                    disabled={!session}
                    value={barcode}
                    onChange={(e) => setBarcode(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        scanBarcode();
                      }
                    }}
                    placeholder={
                      session
                        ? "Scan or Enter Barcode..."
                        : "Start a shift to begin billing"
                    }
                    className="h-16 w-full rounded-2xl border-2 border-gray-200 bg-gray-50 pl-16 pr-6 text-xl font-semibold outline-none focus:border-primary-800"
                  />
                </div>
                <button
                  disabled={!session}
                  onClick={scanBarcode}
                  className="flex h-16 items-center gap-3 rounded-2xl bg-primary-800 px-8 text-lg font-semibold text-white disabled:opacity-50"
                >
                  <ScanLine size={22} />
                  Scan
                </button>
              </div>
              <div className="mt-7">
                <label className="mb-3 block text-sm font-semibold uppercase tracking-wider text-gray-500">
                  Manual Search
                </label>
                <div className="relative">
                  <Search
                    className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                    size={22}
                  />
                  <input
                    disabled={!session}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by Product Name, SKU or Barcode..."
                    className="h-14 w-full rounded-2xl border bg-gray-50 pl-14 pr-5 text-lg outline-none focus:border-primary-800"
                  />
                </div>
                {search && (
                  <div className="mt-5 overflow-hidden rounded-2xl border bg-white shadow-lg">
                    {!results.length ? (
                      <div className="p-8 text-center text-gray-500">
                        No Products Found
                      </div>
                    ) : (
                      results.map((item) => (
                        <div
                          key={item.variantId}
                          className="flex items-center gap-5 border-b p-5"
                        >
                          <img
                            src={imagePlaceholder}
                            alt=""
                            className="h-16 w-16 rounded-2xl object-cover"
                          />

                          <div className="flex-1">
                            <h3 className="text-lg font-bold">
                              {item.product.name}
                            </h3>

                            <p className="mt-1 text-sm text-gray-500">
                              {item.sku} · {item.color?.name || "—"} ·{" "}
                              {item.size?.name || "—"}
                            </p>

                            <p className="mt-1 text-xs font-semibold text-gray-500">
                              {item.availableQuantity ?? "Non-tracked"}{" "}
                              available at this counter store
                            </p>
                            {item.stockByStore?.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5" aria-label="Stock across all stores">
                                {item.stockByStore.map((stock) => (
                                  <span key={stock.store.id} className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700">
                                    {stock.store.name}: <strong>{stock.available}</strong>
                                    {stock.isCounterStore ? " (this counter)" : ""}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>

                          <p className="text-xl font-bold text-primary-800">
                            {money(item.unitPrice)}
                          </p>

                          <div className="flex gap-2">
                            <button
                              type="button"
                              disabled={!item.canSell}
                              onClick={() => addToCart(item)}
                              className="rounded-xl bg-primary-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                            >
                              Add Sale
                            </button>

                            <button
                              type="button"
                              disabled={loadingEligibleReturns}
                              onClick={() => findEligibleReturns(item)}
                              className="rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700"
                            >
                              Find Previous Sale
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {eligibleReturnItems.length > 0 && (
                  <div className="border-t border-red-200 bg-red-50 p-4">
                    <p className="mb-3 text-sm font-bold text-red-800">
                      Eligible Previous Sales
                    </p>

                    <div className="space-y-2">
                      {eligibleReturnItems.map((eligibleItem) => (
                        <div
                          key={eligibleItem.sourceSaleItemId}
                          className="flex items-center justify-between rounded-xl border border-red-100 bg-white p-3"
                        >
                          <div>
                            <p className="font-semibold text-gray-900">
                              {eligibleItem.productName}
                            </p>

                            <p className="mt-1 text-xs text-gray-500">
                              {eligibleItem.receiptNumber} · Returnable:{" "}
                              {eligibleItem.quantityReturnable}
                            </p>

                            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
                              <span>
                                Customer:{" "}
                                <b className="text-gray-800">
                                  {eligibleItem.customer?.fullName ||
                                    "Walk-in Customer"}
                                </b>
                              </span>

                              {eligibleItem.customer?.phone && (
                                <span>
                                  Phone: {eligibleItem.customer.phone}
                                </span>
                              )}

                              {eligibleItem.agentEmployee?.fullName && (
                                <span>
                                  Sold by: {eligibleItem.agentEmployee.fullName}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <b className="block text-red-700">
                                {money(eligibleItem.returnUnitAmount)} each
                              </b>

                              <span className="text-xs text-slate-500">
                                Original line total:{" "}
                                {money(eligibleItem.lineTotalAmount)}
                              </span>
                            </div>

                            <button
                              type="button"
                              onClick={() => addReturnToBill(eligibleItem)}
                              className="rounded-lg bg-red-700 px-3 py-2 text-xs font-bold text-white"
                            >
                              Add Return
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <PosBillItemsTable
            billItems={cart.map((item) => ({
              ...item,
              rowId: item.rowId,
              lineType: item.lineType || "SALE",
              receiptNumber: item.receiptNumber,
              sourceSaleItemId: item.sourceSaleItemId,
              maxQuantity: item.maxQuantity,
              productName: item.product.name,
              taxRate:
                item.lineType === "RETURN"
                  ? (item.taxRate ?? item.product?.gstRate ?? null)
                  : null,

            gstRuleGroup: item.product?.gstRuleGroup ?? null,
            season: item.product.season,
            agentEmployeeId: item.agentEmployeeId || null,
            inventoryLotId: item.inventoryLotId || null,
            availableLots: item.availableLots || [],
            }))}
            agents={agents}
            onAgentSearch={setAgentSearch}
            canOverridePrice={canOverridePrice}
            onQuantityChange={(rowId, quantity) =>
              updateQuantity(rowId, quantity)
            }
            onPriceChange={(rowId, price) => updatePrice(rowId, price)}
            onPriceOverrideReasonChange={(rowId, reason) =>
              updatePriceOverrideReason(rowId, reason)
            }
            lotAllocationMethod={lotAllocationMethod}
            onLotChange={updateLot}
            onAgentChange={(rowId, agentEmployeeId) =>
              setCart((items) =>
                items.map((item) =>
                  item.rowId === rowId
                    ? {
                        ...item,
                        agentEmployeeId: agentEmployeeId || null,
                      }
                    : item,
                ),
              )
            }
            onRemove={(rowId) =>
              setCart((items) => items.filter((item) => item.rowId !== rowId))
            }
          />
          <div className="rounded-3xl bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Clock className="text-primary-800" />
                <div>
                  <h2 className="text-xl font-semibold">Held Bills</h2>
                  <p className="text-sm text-gray-500">
                    Save a bill and resume it later.
                  </p>
                </div>
              </div>

              <button
                type="button"
                disabled={!session || !cart.length}
                onClick={() => setShowHoldBillModal(true)}
                className="rounded-xl bg-primary-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                Hold Current Bill
              </button>
            </div>

            {activeHeldBillId && (
              <div className="mb-4 rounded-2xl border border-primary-200 bg-primary-50 p-4">
                <p className="text-sm font-semibold text-primary-800">
                  A held bill is loaded in this cart.
                </p>

                <p className="mt-1 text-xs text-primary-700">
                  It will automatically be marked recovered after successful
                  payment.
                </p>
              </div>
            )}

            {loadingHeldBills ? (
              <p className="py-8 text-center text-gray-400">
                Loading held bills…
              </p>
            ) : heldBills.length ? (
              <div className="space-y-3">
                {heldBills.map((bill) => {
                  const itemCount = Array.isArray(bill.cartSnapshot?.items)
                    ? bill.cartSnapshot.items.length
                    : 0;

                  return (
                    <div
                      key={bill.id}
                      className="rounded-2xl border border-gray-100 p-4 hover:border-primary-200"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-bold text-primary-800">
                            {bill.holdReference}
                          </p>

                          <p className="mt-1 text-sm font-medium text-gray-900">
                            {bill.customer?.fullName || "Walk-in Customer"}
                          </p>

                          <p className="mt-1 text-xs text-gray-500">
                            {itemCount} item{itemCount === 1 ? "" : "s"} ·{" "}
                            {new Date(bill.heldAt).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </p>
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => handleLoadHeldBill(bill.id)}
                            className="rounded-lg bg-primary-50 px-3 py-2 text-xs font-bold text-primary-800 hover:bg-primary-100"
                          >
                            Load
                          </button>

                          <button
                            type="button"
                            disabled={saving}
                            onClick={() => handleCancelHeldBill(bill.id)}
                            className="rounded-lg bg-red-50 px-3 py-2 text-xs font-bold text-red-700 hover:bg-red-100"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>

                      {bill.note && (
                        <p className="mt-3 border-t pt-3 text-xs text-gray-500">
                          {bill.note}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 py-10 text-center">
                <Clock className="mx-auto text-gray-300" size={30} />
                <p className="mt-3 font-medium text-gray-600">No held bills</p>
                <p className="mt-1 text-sm text-gray-400">
                  Hold a customer’s bill and recover it later.
                </p>
              </div>
            )}
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          <div className="rounded-3xl bg-white p-6 shadow-sm">
            <div className="mb-5 flex items-center gap-3">
              <User className="text-primary-800" />
              <h2 className="text-xl font-semibold">Customer</h2>
            </div>
            {!showCreateCustomer ? (
              <>
                <div className="relative">
                  <Search
                    size={18}
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                  />

                  <input
                    value={customerSearch}
                    onChange={(e) => setCustomerSearch(e.target.value)}
                    placeholder="Search customer by name or phone..."
                    className="w-full rounded-2xl border py-3 pl-11 pr-4 outline-none focus:border-primary-800"
                  />
                </div>

                {customerSearch.trim() && (
                  <div className="mt-3 max-h-52 overflow-y-auto rounded-2xl border bg-white">
                    {customers.length === 0 ? (
                      <p className="p-4 text-sm text-gray-500">
                        No customer found.
                      </p>
                    ) : (
                      customers.map((item) => (
                        <button
                          key={item.id}
                          type="button"
                          onClick={() => {
                            setCustomer(item);
                            setCustomerSearch("");
                          }}
                          className="w-full border-b px-4 py-3 text-left last:border-0 hover:bg-primary-50"
                        >
                          <p className="font-semibold text-gray-900">
                            {item.fullName}
                          </p>

                          <p className="mt-1 text-sm text-gray-500">
                            {item.phone || item.email || "No contact details"}
                          </p>
                        </button>
                      ))
                    )}
                  </div>
                )}

                <div className="mt-5 rounded-2xl bg-[#F8F6F5] p-5">
                  <p className="text-lg font-semibold">
                    {selectedCustomer.fullName}
                  </p>

                  <p className="mt-1 text-gray-500">
                    {selectedCustomer.phone || "Walk-in Customer"}
                  </p>
                </div>

                {customer && (
                  <button
                    type="button"
                    onClick={() => setCustomer(null)}
                    className="mt-3 text-sm font-semibold text-primary-800"
                  >
                    Use Walk-in Customer
                  </button>
                )}

                <button
                  onClick={() => setShowCreateCustomer(true)}
                  className="mt-5 w-full rounded-2xl border border-dashed border-primary-800 py-3 font-semibold text-primary-800"
                >
                  + Create Customer
                </button>
              </>
            ) : (
              <div className="space-y-3">
                <input
                  placeholder="Customer Name"
                  value={newCustomer.fullName}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, fullName: e.target.value })
                  }
                  className="w-full rounded-2xl border p-4"
                />
                <input
                  placeholder="Mobile number (country code below)"
                  value={newCustomer.phone}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, phone: e.target.value })
                  }
                  className="w-full rounded-2xl border p-4"
                />
                <CountryCodeInput value={customerCountryCode} onChange={setCustomerCountryCode} disabled={saving} />
                <input
                  placeholder="Email (Optional)"
                  value={newCustomer.email}
                  onChange={(e) =>
                    setNewCustomer({ ...newCustomer, email: e.target.value })
                  }
                  className="w-full rounded-2xl border p-4"
                />
                <button
                  disabled={saving}
                  onClick={handleCreateCustomer}
                  className="w-full rounded-2xl bg-primary-800 py-4 font-semibold text-white"
                >
                  Save Customer
                </button>
                <button
                  onClick={() => setShowCreateCustomer(false)}
                  className="w-full text-sm text-primary-800"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
          <div className="rounded-3xl bg-gradient-to-br from-primary-800 to-primary-900 p-6 text-white shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-white/80">
                  Available Loyalty Points
                </p>

                <h2 className="mt-2 text-3xl font-bold">
                  {availableLoyaltyPoints.toLocaleString()}
                </h2>

                <p className="mt-1 text-xs text-white/70">
                  1 point = ₹1 discount
                </p>
              </div>

              <Sparkles size={38} />
            </div>

            {customer ? (
              <div className="mt-6 border-t border-white/20 pt-5">
                <label className="text-sm font-semibold text-white">
                  Redeem Points
                </label>

                <div className="mt-2 flex gap-2">
                  <input
                    type="number"
                    min="0"
                    max={Math.min(availableLoyaltyPoints, Math.floor(subtotal))}
                    value={loyaltyPointsToRedeem}
                    onChange={(event) => {
                      const value = event.target.value;

                      if (value === "") {
                        setLoyaltyPointsToRedeem("");
                        return;
                      }

                      setLoyaltyPointsToRedeem(
                        String(Math.max(0, Math.floor(Number(value) || 0))),
                      );
                    }}
                    placeholder="Enter points"
                    className="h-11 min-w-0 flex-1 rounded-xl border border-white/20 bg-white px-3 text-sm font-semibold text-gray-900 outline-none focus:ring-2 focus:ring-primary-300"
                  />

                  <button
                    type="button"
                    disabled={!availableLoyaltyPoints || !subtotal}
                    onClick={() =>
                      setLoyaltyPointsToRedeem(
                        String(
                          Math.min(
                            availableLoyaltyPoints,
                            Math.floor(subtotal),
                          ),
                        ),
                      )
                    }
                    className="rounded-xl bg-white/15 px-3 text-xs font-bold text-white hover:bg-white/25 disabled:opacity-40"
                  >
                    MAX
                  </button>
                </div>

                {requestedLoyaltyPoints > 0 && (
                  <div className="mt-4 rounded-2xl bg-white/10 p-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-white/75">Points redeemed</span>
                      <b>-{redeemableLoyaltyPoints}</b>
                    </div>

                    <div className="mt-2 flex justify-between">
                      <span className="text-white/75">Discount</span>
                      <b>{money(loyaltyDiscountAmount)}</b>
                    </div>
                  </div>
                )}

                <p className="mt-4 text-xs text-white/75">
                  Customer will earn {loyaltyPointsEarned} points after
                  successful payment.
                </p>
              </div>
            ) : (
              <p className="mt-5 text-sm text-white/80">
                Select a customer to redeem or earn loyalty points.
              </p>
            )}
          </div>
          <div className="rounded-3xl bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center gap-3">
              <Tag className="text-primary-800" />
              <div>
                <h2 className="text-xl font-semibold">Coupon</h2>
                <p className="text-sm text-gray-500">
                  Apply a promotion code for this customer.
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <input
                disabled={!customer || !cart.length || Boolean(appliedCoupon)}
                value={couponInput}
                onChange={(event) =>
                  setCouponInput(event.target.value.toUpperCase())
                }
                placeholder="Enter coupon code"
                className="h-11 min-w-0 flex-1 rounded-xl border px-3 text-sm font-semibold uppercase outline-none focus:border-primary-800 disabled:bg-gray-50"
              />

              <button
                type="button"
                disabled={
                  !customer ||
                  !cart.length ||
                  !couponInput.trim() ||
                  applyingCoupon ||
                  Boolean(appliedCoupon)
                }
                onClick={handleApplyCoupon}
                className="rounded-xl bg-primary-800 px-4 text-sm font-bold text-white disabled:opacity-50"
              >
                {applyingCoupon ? "Checking…" : "Apply"}
              </button>
            </div>

            {!customer && (
              <p className="mt-3 text-xs text-gray-500">
                Select a customer to use a coupon.
              </p>
            )}

            {appliedCoupon && (
              <div className="mt-4 rounded-2xl border border-green-200 bg-green-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-green-800">
                      {appliedCoupon.code}
                    </p>
                    <p className="mt-1 text-sm text-green-700">
                      {appliedCoupon.promotionName}
                    </p>
                    <p className="mt-2 text-sm font-semibold text-green-800">
                      Discount: {money(appliedCoupon.discountAmount)}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setAppliedCoupon(null);
                      setCouponInput("");
                    }}
                    className="text-sm font-semibold text-primary-800"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}
          </div>
          <div className="rounded-3xl bg-white p-6 shadow-sm">
            <div className="mb-6 flex items-center gap-3">
              <CreditCard className="text-primary-800" />

              <div>
                <h2 className="text-xl font-semibold">Bill Summary</h2>

                <p className="mt-1 text-sm text-gray-500">
                  Sales, returns, loyalty, and discounts in one bill.
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-500">New Sale Items</span>
                <b>{money(saleItemsTotal)}</b>
              </div>

              {returnItemsTotal > 0 && (
                <div className="flex justify-between text-red-700">
                  <span>Return Credit</span>
                  <b>-{money(returnItemsTotal)}</b>
                </div>
              )}

              {restoredLoyaltyPoints > 0 && (
                <div className="flex justify-between text-violet-700">
                  <span>Points restored from returned item</span>
                  <b>+{restoredLoyaltyPoints}</b>
                </div>
              )}

              {restoredPointsAppliedToReplacement > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Restored points used on replacement</span>
                  <b>-{money(restoredPointsAppliedToReplacement)}</b>
                </div>
              )}

              {unusedRestoredLoyaltyPoints > 0 && (
                <div className="rounded-xl bg-violet-50 px-3 py-2 text-xs font-medium text-violet-800">
                  {unusedRestoredLoyaltyPoints} points will be returned to the
                  customer’s loyalty wallet.
                </div>
              )}

              {!hasReturnLines && couponDiscountAmount > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Coupon ({appliedCoupon?.code})</span>
                  <b>-{money(couponDiscountAmount)}</b>
                </div>
              )}

              {!hasReturnLines && loyaltyDiscountAmount > 0 && (
                <div className="flex justify-between text-green-700">
                  <span>Loyalty Redemption</span>
                  <b>-{money(loyaltyDiscountAmount)}</b>
                </div>
              )}

              {!hasReturnLines && saleRoundOffAmount !== 0 && (
                <div className="flex justify-between text-gray-600">
                  <span>Round off</span>
                  <b>{saleRoundOffAmount > 0 ? "+" : ""}{money(saleRoundOffAmount)}</b>
                </div>
              )}

              <PosGstTotal items={saleBillLines} returnItems={returnBillLines} customerId={customer?.id} couponCode={hasReturnLines ? '' : appliedCoupon?.code} loyaltyPointsToRedeem={hasReturnLines ? 0 : requestedLoyaltyPoints} accessToken={accessToken} />

              {customer && settlementTotal > 0 && (
                <div className="flex justify-between text-primary-800">
                  <span>Points to Earn</span>
                  <b>+{loyaltyPointsEarned}</b>
                </div>
              )}

              {hasReturnLines && (
                <div className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Coupons and manually redeemed points are unavailable for
                  return and exchange bills. Historic loyalty redemption is
                  restored automatically.
                </div>
              )}

              <hr />

              <div className="flex justify-between text-2xl font-bold">
                <span>{settlementLabel}</span>

                <span
                  className={
                    settlementTotal < 0
                      ? "text-red-700"
                      : settlementTotal === 0
                        ? "text-emerald-700"
                        : "text-primary-800"
                  }
                >
                  {money(Math.abs(settlementTotal))}
                </span>
              </div>
            </div>

            <button
              disabled={!session || !cart.length}
              onClick={() => {
                setPaymentAmount(Math.abs(settlementTotal).toFixed(2));
                setShowPayment(true);
              }}
              className="mt-8 w-full rounded-2xl bg-primary-800 py-4 text-lg font-semibold text-white disabled:opacity-50"
            >
              {settlementTotal > 0
                ? "Continue to Payment"
                : settlementTotal < 0
                  ? "Continue to Refund"
                  : "Complete Equal Exchange"}
            </button>
          </div>
        </div>
      </div>

      {showHoldBillModal && (
        <Modal>
          <div className="w-full max-w-md rounded-3xl bg-white p-7">
            <h2 className="text-2xl font-bold">Hold Current Bill</h2>

            <p className="mt-2 text-gray-500">
              This saves the cart only. Stock, loyalty points, coupons, and
              payments are not affected until payment is completed.
            </p>

            <textarea
              value={holdNote}
              onChange={(event) => setHoldNote(event.target.value)}
              placeholder="Optional note, customer request, or reference..."
              rows={4}
              className="mt-6 w-full rounded-2xl border p-4 outline-none focus:border-primary-800"
            />

            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setShowHoldBillModal(false)}
                className="flex-1 rounded-2xl border border-gray-200 py-3 font-semibold text-gray-700"
              >
                Cancel
              </button>

              <button
                disabled={saving}
                type="button"
                onClick={handleHoldBill}
                className="flex-1 rounded-2xl bg-primary-800 py-3 font-semibold text-white disabled:opacity-50"
              >
                {saving ? "Holding…" : "Hold Bill"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {showOpen && (
        <Modal>
          <div className="w-full max-w-md rounded-3xl bg-white p-7">
            <h2 className="text-2xl font-bold">Start POS Shift</h2>
            <p className="mt-1 text-gray-500">
              Choose your assigned counter and opening cash.
            </p>
            <select
              value={selectedCounterId}
              onChange={(e) => setSelectedCounterId(e.target.value)}
              className="mt-6 w-full rounded-2xl border p-4"
            >
              {counters.map((counter) => (
                <option key={counter.id} value={counter.id}>
                  {counter.name} · {counter.store.name}
                </option>
              ))}
            </select>
            <input
              type="number"
              min="0"
              value={openingCash}
              onChange={(e) => setOpeningCash(e.target.value)}
              placeholder="Opening cash"
              className="mt-4 w-full rounded-2xl border p-4"
            />
            <button
              disabled={saving || !selectedCounterId}
              onClick={handleOpenSession}
              className="mt-5 w-full rounded-2xl bg-primary-800 py-4 font-semibold text-white"
            >
              {saving ? "Starting…" : "Start Shift"}
            </button>
          </div>
        </Modal>
      )}

      <PosCloseShiftModal
        open={showClose}
        session={session}
        accessToken={accessToken}
        actualCash={actualCash}
        setActualCash={setActualCash}
        saving={saving}
        onClose={() => setShowClose(false)}
        onConfirm={handleCloseSession}
      />

      {showPayment && (
        <Modal>
          <div className="w-full max-w-md rounded-3xl bg-white p-7">
            <h2 className="text-2xl font-bold">
              {settlementTotal < 0 ? "Refund Settlement" : "Payment Settlement"}
            </h2>

            <p className="mt-1 text-gray-500">
              {settlementTotal > 0
                ? `Collect ${money(settlementTotal)} from the customer.`
                : settlementTotal < 0
                  ? `Refund ${money(Math.abs(settlementTotal))} to the customer.`
                  : "No cash settlement is required for this exchange."}
            </p>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="mt-6 w-full rounded-2xl border p-4"
            >
              <option value="CASH">Cash</option>
              <option value="UPI">UPI</option>
              <option value="CARD">Card</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
              {hasReturnLines && (
                <option value="LOYALTY_POINTS">Loyalty Points</option>
              )}
              <option value="OTHER">Other</option>
            </select>
            <input
              type="number"
              min="0"
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
              className="mt-4 w-full rounded-2xl border p-4"
              placeholder="Received amount"
            />
            <input
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              className="mt-4 w-full rounded-2xl border p-4"
              placeholder="UPI/card reference (optional)"
            />
            <button
              disabled={saving}
              onClick={handlePayment}
              className="mt-5 w-full rounded-2xl bg-primary-800 py-4 font-semibold text-white"
            >
              {saving ? "Completing…" : "Complete Sale"}
            </button>
            <button
              onClick={() => setShowPayment(false)}
              className="mt-3 w-full text-sm text-primary-800"
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      <PosReturnModal
        open={showReturnModal}
        accessToken={accessToken}
        onClose={() => setShowReturnModal(false)}
        onSuccess={async (posReturn) => {
          setSuccess(
            `Return completed — ${posReturn.returnReference}. Refund: ${money(
              posReturn.refundAmount,
            )}`,
          );

          await loadSession();
        }}
      />

      <PosTransactionHistoryModal
        open={showTransactionHistory}
        onClose={() => setShowTransactionHistory(false)}
        accessToken={accessToken}
        canVoid={hasPermission(user, "pos.void_sale")}
        onSaleVoided={async (voidedSale) => {
          setSuccess(
            `Sale ${voidedSale.receiptNumber} was voided successfully.`,
          );
          await loadSession();
        }}
      />
    </div>
  );
}
